/**
 * The country the ORGANISATION is registered in — its legal and compliance
 * home, and the field every downstream product derives its tax regime and base
 * currency from (sk-billing turns it into a region pack + base currency, and
 * both are IMMUTABLE once activated).
 *
 * Why this is its own field rather than the flag inside the phone input: the
 * mobile input's country picker sets a DIAL CODE. It happened to also carry the
 * ISO code, so signup sent whatever the phone flag said — meaning a UAE
 * operator who typed a local number registered as India and could never be
 * billed in AED afterwards. A commercial country and a phone country are not
 * the same fact and must not share one control.
 *
 * The dropdown is OWNED here rather than borrowed from mobileNumberInput. That
 * borrowing put "+93" and "9 digits" on every row — phone facts, meaningless
 * when the question is where a company is registered. Only the COUNTRIES data
 * is shared; the presentation is this field's own.
 */
import React, { useState, useRef, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
  FlatList,
  TextInput,
  Dimensions,
  Platform,
} from 'react-native';
import OurIcon from './icon';
import RequiredMark from './requiredMark';
import { useTheme } from '../context/themeContext';
import { COUNTRIES, type CountryEntry } from './mobileNumberInput';

export interface CountrySelectProps {
  readonly label?: string;
  readonly required?: boolean;
  readonly value: CountryEntry;
  readonly onChange: (country: CountryEntry) => void;
  readonly editable?: boolean;
  readonly error?: string;
  /** Shown in brackets beside the label — it qualifies the question, so it
   *  belongs with it rather than as a footnote read after answering. */
  readonly hint?: string;
}

const ROW_HEIGHT = 44;
const DROPDOWN_MIN_WIDTH = 280;
const DROPDOWN_MAX_HEIGHT = 340;

const CountrySelect: React.FC<CountrySelectProps> = ({
  label = 'COUNTRY',
  required = false,
  value,
  onChange,
  editable = true,
  error,
  hint,
}) => {
  const { theme } = useTheme();
  const C = theme.colors;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [anchor, setAnchor] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const rowRef = useRef<View>(null);

  const openPicker = () => {
    if (!editable) return;
    rowRef.current?.measureInWindow((x, y, width, height) => {
      setAnchor({ x, y, width, height });
      setOpen(true);
    });
  };

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  // Matching the ISO code too is invisible but useful — typing "AE" finds the
  // UAE without the code ever being shown.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [search]);

  const screen = Dimensions.get('window');
  const width = anchor
    ? Math.min(screen.width - 16, Math.max(DROPDOWN_MIN_WIDTH, anchor.width))
    : DROPDOWN_MIN_WIDTH;
  const left = anchor ? Math.max(8, Math.min(anchor.x, screen.width - width - 8)) : 8;
  const spaceBelow = anchor ? screen.height - (anchor.y + anchor.height) - 12 : 0;
  const spaceAbove = anchor ? anchor.y - 12 : 0;
  const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
  const maxHeight = Math.min(DROPDOWN_MAX_HEIGHT, Math.max(180, openUp ? spaceAbove : spaceBelow));
  const position = anchor
    ? openUp
      ? { bottom: screen.height - anchor.y + 6 }
      : { top: anchor.y + anchor.height + 6 }
    : {};

  return (
    <View style={styles.wrap}>
      <View style={styles.labelRow}>
        <Text style={[styles.label, { color: C.ink }]}>{label}</Text>
        {required ? <RequiredMark /> : null}
        {hint ? (
          <Text style={[styles.hint, { color: C.ink3 }]} numberOfLines={1}>
            ({hint})
          </Text>
        ) : null}
      </View>

      <TouchableOpacity
        ref={rowRef}
        activeOpacity={0.8}
        onPress={openPicker}
        disabled={!editable}
        style={[
          styles.box,
          !editable && { opacity: 0.6 },
          { backgroundColor: C.card, borderColor: error ? C.danger : C.line },
        ]}
      >
        <Text style={styles.flag}>{value.flag}</Text>
        <Text style={[styles.name, { color: C.ink }]} numberOfLines={1}>
          {value.name}
        </Text>
        <OurIcon name="chev-d" size={16} color={C.ink3} />
      </TouchableOpacity>

      {error ? <Text style={[styles.error, { color: C.danger }]}>{error}</Text> : null}

      <Modal visible={open} transparent animationType="fade" onRequestClose={close}>
        <Pressable style={StyleSheet.absoluteFill} onPress={close} />
        <View
          style={[
            styles.panel,
            position,
            { left, width, maxHeight, borderColor: C.line, backgroundColor: C.card },
          ]}
        >
          <View style={[styles.searchRow, { borderBottomColor: C.line, backgroundColor: C.paper2 }]}>
            <OurIcon name="search" size={16} color={C.ink4} />
            <TextInput
              style={[
                styles.searchInput,
                { color: C.ink },
                Platform.select({ web: { outlineStyle: 'none' } as object }),
              ]}
              placeholder="Search country..."
              placeholderTextColor={C.ink4}
              value={search}
              onChangeText={setSearch}
              autoFocus
            />
            {search.length > 0 && (
              <TouchableOpacity onPress={() => setSearch('')}>
                <OurIcon name="x" size={16} color={C.ink4} />
              </TouchableOpacity>
            )}
          </View>

          <FlatList
            data={filtered}
            keyExtractor={(item) => item.code}
            keyboardShouldPersistTaps="handled"
            getItemLayout={(_, index) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index })}
            renderItem={({ item }) => {
              const selected = item.code === value.code;
              return (
                <TouchableOpacity
                  style={[
                    styles.row,
                    { borderBottomColor: C.line, backgroundColor: C.card },
                    selected && { backgroundColor: C.accentSoft },
                  ]}
                  onPress={() => {
                    onChange(item);
                    close();
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={styles.rowFlag}>{item.flag}</Text>
                  <Text style={[styles.rowName, { color: C.ink }]} numberOfLines={1}>
                    {item.name}
                  </Text>
                  {selected ? <OurIcon name="check" size={15} color={C.accent} /> : null}
                </TouchableOpacity>
              );
            }}
            ListEmptyComponent={
              <Text style={{ padding: 16, color: C.ink4, fontSize: 13 }}>
                No countries match “{search}”.
              </Text>
            }
            style={{ backgroundColor: C.card }}
            showsVerticalScrollIndicator={false}
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  // Matches CommonInput's container (marginBottom 18) so this field sits in the
  // same rhythm as Company Name above it — it owns its own trailing gap rather
  // than leaning on the caller, which is why it crowded the phone field.
  wrap: { width: '100%', marginBottom: 18 },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  hint: {
    flex: 1,
    fontSize: 11,
    fontWeight: '500',
    marginLeft: 6,
    letterSpacing: 0,
    textTransform: 'none',
  },
  box: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 42,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1.5,
    borderRadius: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 4,
    elevation: 1,
  },
  flag: { fontSize: 18 },
  name: { flex: 1, fontSize: 14, fontWeight: '600' },
  error: { fontSize: 11, marginTop: 4, paddingHorizontal: 4 },
  panel: {
    position: 'absolute',
    borderWidth: 1,
    borderRadius: 4,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 20,
    elevation: 12,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  searchInput: { flex: 1, fontSize: 13, padding: 0 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    height: ROW_HEIGHT,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowFlag: { fontSize: 18 },
  rowName: { flex: 1, fontSize: 13, fontWeight: '600' },
});

export default CountrySelect;
